import React, { useState, useEffect } from "react";
import { complianceAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import {
  ClipboardCheck, Truck, MessageSquare, CheckCircle, AlertTriangle,
  Plus, Trash2, X, Check, RefreshCw, Loader, Star, Camera, Key,
  ShieldCheck, Filter
} from "lucide-react";
import { useConfirm } from "../hooks/useConfirm";

const TABS = [
  { id: "smc", label: "SMC Tasting", icon: ClipboardCheck },
  { id: "delivery", label: "Delivery Confirmation", icon: Truck },
  { id: "complaints", label: "Complaints", icon: MessageSquare },
];

const COMPLAINT_TYPES = [
  "late_delivery", "short_quantity", "quality_issue", "missed_day",
  "wrong_menu", "vessel_issue", "hygiene_issue", "other"
];

const COMPLAINT_STATUS_TABS = ["open", "in_progress", "resolved", "all"];

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}>
          <Star size={20} fill={n <= value ? "#E8956D" : "none"} className={n <= value ? "text-[#E8956D]" : "text-[#C4BAAF]"} />
        </button>
      ))}
    </div>
  );
}

const generateOTP = () => String(Math.floor(1000 + Math.random() * 9000));

export default function SchoolCompliance() {
  const { selectedKitchen, kitchens: ctxKitchens } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  const { confirm, ConfirmModal } = useConfirm();

  const [tab, setTab] = useState("smc");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  // SMC
  const [smcList, setSmcList] = useState([]);
  const [smcStats, setSmcStats] = useState(null);
  const [smcForm, setSmcForm] = useState({ date, school_name: "", route_number: "", tasted_by: "", meal_quality: 4, remarks: "", approved: true });
  const [showSmcForm, setShowSmcForm] = useState(false);

  // Delivery
  const [deliveries, setDeliveries] = useState([]);
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [deliveryForm, setDeliveryForm] = useState({ date, school_name: "", route_number: "", confirmed_by: "", quantity_received: "", meal_received: true, delivery_time: "", otp_code: "", remarks: "" });
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [otpGenerated, setOtpGenerated] = useState("");

  // Complaints
  const [complaints, setComplaints] = useState([]);
  const [complaintStats, setComplaintStats] = useState(null);
  const [complaintFilter, setComplaintFilter] = useState("open");
  const [complaintForm, setComplaintForm] = useState({ date, school_name: "", route_number: "", complaint_type: "late_delivery", description: "", severity: "medium", reported_by: "", reporter_contact: "" });
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [resolveModal, setResolveModal] = useState(null); // { id, school_name }
  const [resolveNotes, setResolveNotes] = useState("");

  useEffect(() => {
    if (ctxKitchens?.length > 0) setKitchens(ctxKitchens);
    else usersAPI.getKitchens().then(r => setKitchens(r.data || [])).catch(() => {});
  }, [ctxKitchens]);

  useEffect(() => {
    if (tab === "smc") { fetchSMC(); }
    if (tab === "delivery") { fetchDeliveries(); fetchPending(); }
    if (tab === "complaints") { fetchComplaints(); }
  }, [tab, date, selectedKitchen]);

  const q = () => ({ date, kitchen_id: selectedKitchen || null });

  const fetchSMC = async () => {
    setLoading(true);
    try {
      const [list, stats] = await Promise.all([
        complianceAPI.getSMCTasting(q()),
        complianceAPI.getSMCStats({ kitchen_id: selectedKitchen || null, from_date: date, to_date: date })
      ]);
      setSmcList(list.data || []); setSmcStats(stats.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      const r = await complianceAPI.getDeliveryConfirmations(q());
      setDeliveries(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const fetchPending = async () => {
    try {
      const r = await complianceAPI.getPendingDeliveries(q());
      setPendingDeliveries(r.data?.pending_schools || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const [list, stats] = await Promise.all([
        complianceAPI.getComplaints({ status: complaintFilter, kitchen_id: selectedKitchen || null }),
        complianceAPI.getComplaintStats({ kitchen_id: selectedKitchen || null })
      ]);
      setComplaints(list.data || []); setComplaintStats(stats.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSaveSMC = async () => {
    try {
      await complianceAPI.createSMCTasting({ ...smcForm, kitchen_id: selectedKitchen || null });
      setMsg("SMC tasting record saved!");
      setShowSmcForm(false);
      setSmcForm({ date, school_name: "", route_number: "", tasted_by: "", meal_quality: 4, remarks: "", approved: true });
      fetchSMC();
    } catch (e) { setError(formatError(e)); }
  };

  const handleSaveDelivery = async () => {
    try {
      await complianceAPI.createDeliveryConfirmation({ ...deliveryForm, kitchen_id: selectedKitchen || null });
      setMsg("Delivery confirmed!");
      setShowDeliveryForm(false);
      setOtpGenerated("");
      setDeliveryForm({ date, school_name: "", route_number: "", confirmed_by: "", quantity_received: "", meal_received: true, delivery_time: "", otp_code: "", remarks: "" });
      fetchDeliveries(); fetchPending();
    } catch (e) { setError(formatError(e)); }
  };

  const handleSaveComplaint = async () => {
    try {
      await complianceAPI.createComplaint({ ...complaintForm, kitchen_id: selectedKitchen || null });
      setMsg("Complaint recorded!");
      setShowComplaintForm(false);
      setComplaintForm({ date, school_name: "", route_number: "", complaint_type: "late_delivery", description: "", severity: "medium", reported_by: "", reporter_contact: "" });
      fetchComplaints();
    } catch (e) { setError(formatError(e)); }
  };

  const handleResolveComplaint = async () => {
    if (!resolveModal || !resolveNotes.trim()) return;
    await complianceAPI.updateComplaint(resolveModal.id, { status: "resolved", resolution_notes: resolveNotes });
    setResolveModal(null); setResolveNotes("");
    fetchComplaints();
  };

  const handleMarkInProgress = async (id) => {
    await complianceAPI.updateComplaint(id, { status: "in_progress" });
    fetchComplaints();
  };

  // Refetch complaints when filter changes
  useEffect(() => {
    if (tab === "complaints") fetchComplaints();
  }, [complaintFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5 max-w-5xl" data-testid="school-compliance">
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">School Compliance</h1>
        <p className="text-sm text-[#8A7E73] mt-0.5">SMC tasting records, delivery confirmations, and school complaints</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 -mb-px transition-colors ${tab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm flex items-center gap-2"><CheckCircle size={14}/>{msg}</div>}

      {/* Date filter */}
      <div className="flex items-center gap-3">
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        {loading && <Loader size={16} className="animate-spin text-[#E8956D]" />}
      </div>

      {/* ── SMC TASTING ── */}
      {tab === "smc" && (
        <div className="space-y-4">
          {smcStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total Records", value: smcStats.total, color: "text-[#3A322C]" },
                { label: "Approved", value: smcStats.approved, color: "text-[#637C64]" },
                { label: "Rejected", value: smcStats.rejected, color: "text-[#C2624E]" },
                { label: "Low Quality (≤2)", value: smcStats.low_quality, color: "text-[#D98032]" },
              ].map(s => (
                <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">{s.label}</p>
                  <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={() => setShowSmcForm(!showSmcForm)}
              className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1">
              <Plus size={14} /> Add SMC Record
            </button>
          </div>

          {showSmcForm && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-3" data-testid="smc-form">
              <h3 className="font-bold text-[#3A322C]">New SMC Tasting Record</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">School Name *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={smcForm.school_name} onChange={e => setSmcForm(f => ({ ...f, school_name: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route No.</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={smcForm.route_number} onChange={e => setSmcForm(f => ({ ...f, route_number: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Tasted By (SMC Member) *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={smcForm.tasted_by} onChange={e => setSmcForm(f => ({ ...f, tasted_by: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Time</label>
                  <input type="time" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={smcForm.tasted_at} onChange={e => setSmcForm(f => ({ ...f, tasted_at: e.target.value }))} /></div>
              </div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Meal Quality *</label>
                <StarRating value={smcForm.meal_quality} onChange={v => setSmcForm(f => ({ ...f, meal_quality: v }))} /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={smcForm.approved} onChange={e => setSmcForm(f => ({ ...f, approved: e.target.checked }))} />
                <span className="font-medium text-[#3A322C]">Meal Approved by SMC</span>
              </label>
              <textarea rows={2} placeholder="Remarks (quality issues, suggestions...)" value={smcForm.remarks}
                onChange={e => setSmcForm(f => ({ ...f, remarks: e.target.value }))}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" />
              <div className="flex gap-3">
                <button onClick={() => setShowSmcForm(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleSaveSMC} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2.5 text-sm font-bold">Save Record</button>
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            {smcList.length === 0 ? (
              <div className="text-center py-10 text-[#8A7E73] text-sm">No SMC records for {date}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F4F3F0]">
                  <tr>{["School", "Route", "Tasted By", "Quality", "Status", "Remarks"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {smcList.map(r => (
                    <tr key={r.id} className="hover:bg-[#FAFAF8]">
                      <td className="px-4 py-3 font-medium text-[#3A322C] text-xs">{r.school_name}</td>
                      <td className="px-4 py-3 text-xs text-[#E8956D] font-bold">{r.route_number || "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5147]">{r.tasted_by}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-0.5">{[1,2,3,4,5].map(n => (
                          <Star key={n} size={12} fill={n <= r.meal_quality ? "#E8956D" : "none"} className={n <= r.meal_quality ? "text-[#E8956D]" : "text-[#C4BAAF]"} />
                        ))}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.approved ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                          {r.approved ? "Approved" : "Rejected"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#8A7E73] max-w-[150px] truncate">{r.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── DELIVERY CONFIRMATION ── */}
      {tab === "delivery" && (
        <div className="space-y-4">
          {pendingDeliveries.length > 0 && (
            <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3">
              <p className="text-sm font-bold text-[#D98032] mb-2">{pendingDeliveries.length} school(s) not confirmed for {date}</p>
              <div className="flex flex-wrap gap-1">
                {pendingDeliveries.slice(0, 8).map(s => (
                  <span key={s} className="text-xs bg-white border border-[#D98032]/30 px-2 py-0.5 rounded-lg text-[#5C5147]">{s}</span>
                ))}
                {pendingDeliveries.length > 8 && <span className="text-xs text-[#D98032]">+{pendingDeliveries.length - 8} more</span>}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={() => setShowDeliveryForm(!showDeliveryForm)}
              className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1">
              <Plus size={14} /> Confirm Delivery
            </button>
          </div>

          {showDeliveryForm && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-3">
              <h3 className="font-bold text-[#3A322C]">Delivery Confirmation</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">School Name *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={deliveryForm.school_name} onChange={e => setDeliveryForm(f => ({ ...f, school_name: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route No.</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={deliveryForm.route_number} onChange={e => setDeliveryForm(f => ({ ...f, route_number: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Confirmed By (Headmaster/In-charge) *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={deliveryForm.confirmed_by} onChange={e => setDeliveryForm(f => ({ ...f, confirmed_by: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Delivery Time</label>
                  <input type="time" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={deliveryForm.delivery_time} onChange={e => setDeliveryForm(f => ({ ...f, delivery_time: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Quantity Received (kg)</label>
                  <input type="number" step="0.1" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={deliveryForm.quantity_received} onChange={e => setDeliveryForm(f => ({ ...f, quantity_received: e.target.value }))} /></div>
                <div className="flex items-center gap-2 mt-5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={deliveryForm.meal_received} onChange={e => setDeliveryForm(f => ({ ...f, meal_received: e.target.checked }))} />
                    <span className="font-medium">Meal Received</span>
                  </label>
                </div>
              </div>

              {/* OTP Section */}
              <div className="bg-[#F4F3F0] rounded-xl p-4 space-y-2">
                <p className="text-xs font-bold uppercase text-[#8A7E73]">Headmaster OTP Confirmation</p>
                {otpGenerated ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-[#E8956D]/10 border-2 border-[#E8956D] rounded-xl p-3 text-center">
                      <p className="text-xs text-[#8A7E73] mb-1">Show this code to headmaster</p>
                      <p className="text-3xl font-extrabold text-[#E8956D] tracking-[0.4em]">{otpGenerated}</p>
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-xs text-[#5C5147]">Headmaster acknowledges delivery. Enter the same code below to confirm:</p>
                      <input
                        type="text" maxLength={4} placeholder="Enter OTP"
                        value={deliveryForm.otp_code}
                        onChange={e => setDeliveryForm(f => ({ ...f, otp_code: e.target.value }))}
                        className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] text-center font-bold tracking-widest text-lg"
                      />
                      {deliveryForm.otp_code && deliveryForm.otp_code === otpGenerated && (
                        <p className="text-xs text-[#637C64] font-bold flex items-center gap-1"><ShieldCheck size={12} /> OTP Verified</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { const otp = generateOTP(); setOtpGenerated(otp); setDeliveryForm(f => ({ ...f, otp_code: "" })); }}
                    className="flex items-center gap-2 text-sm bg-[#E8956D]/10 border border-[#E8956D]/30 text-[#E8956D] rounded-lg px-4 py-2 font-bold hover:bg-[#E8956D]/20">
                    <Key size={14} /> Generate Handoff OTP
                  </button>
                )}
              </div>

              <textarea rows={2} placeholder="Remarks..." value={deliveryForm.remarks}
                onChange={e => setDeliveryForm(f => ({ ...f, remarks: e.target.value }))}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" />
              <div className="flex gap-3">
                <button onClick={() => { setShowDeliveryForm(false); setOtpGenerated(""); }} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleSaveDelivery} className="flex-1 bg-[#637C64] text-white rounded-lg py-2.5 text-sm font-bold">Confirm</button>
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            {deliveries.length === 0 ? (
              <div className="text-center py-10 text-[#8A7E73] text-sm">No delivery confirmations for {date}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F4F3F0]">
                  <tr>{["School", "Route", "Time", "Confirmed By", "Qty", "OTP", "Status"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {deliveries.map(d => (
                    <tr key={d.id} className="hover:bg-[#FAFAF8]">
                      <td className="px-4 py-3 font-medium text-[#3A322C] text-xs">{d.school_name}</td>
                      <td className="px-4 py-3 text-xs text-[#E8956D] font-bold">{d.route_number || "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5147]">{d.delivery_time || "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5147]">{d.confirmed_by}</td>
                      <td className="px-4 py-3 text-xs font-bold text-[#5C5147]">{d.quantity_received ? `${d.quantity_received} kg` : "—"}</td>
                      <td className="px-4 py-3">
                        {d.otp_code ? (
                          <span className="font-mono font-bold text-[#637C64] text-xs bg-[#EDF2EE] px-2 py-0.5 rounded-lg flex items-center gap-1 w-fit">
                            <ShieldCheck size={10} /> {d.otp_code}
                          </span>
                        ) : <span className="text-xs text-[#C4BAAF]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${d.meal_received ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                          {d.meal_received ? "Received" : "NOT Received"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── COMPLAINTS ── */}
      {tab === "complaints" && (
        <div className="space-y-4">
          {complaintStats && (
            <div className="flex gap-3 flex-wrap">
              <span className="bg-[#FBEAE7] text-[#C2624E] font-bold px-3 py-1.5 rounded-lg text-sm">{complaintStats.open} Open</span>
              <span className="bg-[#F4F3F0] text-[#5C5147] font-bold px-3 py-1.5 rounded-lg text-sm">{complaintStats.total} Total</span>
              {(complaintStats.by_type || []).map(t => (
                <span key={t.type} className="bg-[#F4F3F0] text-[#8A7E73] px-3 py-1.5 rounded-lg text-xs">{t.type.replace(/_/g," ")}: {t.count}</span>
              ))}
            </div>
          )}

          {/* Status Filter Tabs */}
          <div className="flex gap-1 bg-[#F4F3F0] p-1 rounded-xl w-fit">
            {COMPLAINT_STATUS_TABS.map(s => (
              <button key={s} onClick={() => setComplaintFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-colors ${complaintFilter === s ? "bg-white text-[#3A322C] shadow-sm" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
                {s === "in_progress" ? "In Progress" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex justify-end">
            <button onClick={() => setShowComplaintForm(!showComplaintForm)}
              className="bg-[#C2624E] text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1">
              <Plus size={14} /> Log Complaint
            </button>
          </div>

          {showComplaintForm && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-3">
              <h3 className="font-bold text-[#3A322C]">New School Complaint</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">School Name *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={complaintForm.school_name} onChange={e => setComplaintForm(f => ({ ...f, school_name: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route No.</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={complaintForm.route_number} onChange={e => setComplaintForm(f => ({ ...f, route_number: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Complaint Type *</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={complaintForm.complaint_type} onChange={e => setComplaintForm(f => ({ ...f, complaint_type: e.target.value }))}>
                    {COMPLAINT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
                  </select></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Severity</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={complaintForm.severity} onChange={e => setComplaintForm(f => ({ ...f, severity: e.target.value }))}>
                    {["low","medium","high","critical"].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                  </select></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reported By</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={complaintForm.reported_by} onChange={e => setComplaintForm(f => ({ ...f, reported_by: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Contact</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={complaintForm.reporter_contact} onChange={e => setComplaintForm(f => ({ ...f, reporter_contact: e.target.value }))} /></div>
              </div>
              <textarea rows={3} placeholder="Describe the issue in detail *" value={complaintForm.description}
                onChange={e => setComplaintForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" />
              <div className="flex gap-3">
                <button onClick={() => setShowComplaintForm(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleSaveComplaint} className="flex-1 bg-[#C2624E] text-white rounded-lg py-2.5 text-sm font-bold">Submit Complaint</button>
              </div>
            </div>
          )}

          {loading && <div className="flex justify-center py-6"><Loader size={20} className="animate-spin text-[#E8956D]" /></div>}

          <div className="space-y-3">
            {complaints.length === 0 ? (
              <div className="text-center py-10 bg-white border border-[#E5E1DB] rounded-xl text-[#8A7E73] text-sm">
                No complaints with status "{complaintFilter}"
              </div>
            ) : complaints.map(c => (
              <div key={c.id} className={`bg-white border rounded-xl p-4 ${c.severity === "critical" ? "border-[#C2624E]" : c.severity === "high" ? "border-[#D98032]/50" : "border-[#E5E1DB]"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-bold text-[#3A322C] text-sm">{c.school_name}</span>
                      {c.route_number && <span className="text-xs text-[#E8956D] font-bold">Route {c.route_number}</span>}
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${c.severity === "critical" ? "bg-[#FBEAE7] text-[#C2624E]" : c.severity === "high" ? "bg-[#FDF4EB] text-[#D98032]" : "bg-[#F4F3F0] text-[#8A7E73]"}`}>
                        {c.severity}
                      </span>
                      <span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{c.complaint_type?.replace(/_/g," ")}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.status === "open" ? "bg-[#FBEAE7] text-[#C2624E]" : c.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                        {c.status === "in_progress" ? "In Progress" : c.status}
                      </span>
                    </div>
                    <p className="text-sm text-[#5C5147]">{c.description}</p>
                    <p className="text-xs text-[#8A7E73] mt-1">Reported by: {c.reported_by} · {c.date}</p>
                    {c.resolution_notes && <p className="text-xs text-[#637C64] mt-1 italic">Resolution: {c.resolution_notes}</p>}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {c.status === "open" && (
                      <button onClick={() => handleMarkInProgress(c.id)}
                        className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-1">
                        In Progress
                      </button>
                    )}
                    {(c.status === "open" || c.status === "in_progress") && (
                      <button onClick={() => { setResolveModal({ id: c.id, school_name: c.school_name }); setResolveNotes(""); }}
                        className="text-xs bg-[#637C64] text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-1">
                        <Check size={11} /> Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {ConfirmModal}

      {/* Resolve Complaint Modal */}
      {resolveModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C] font-[Manrope]">Resolve Complaint</h2>
              <button onClick={() => setResolveModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#5C5147]">School: <span className="font-bold">{resolveModal.school_name}</span></p>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Resolution Notes *</label>
                <textarea rows={4} placeholder="Describe how this complaint was resolved..."
                  value={resolveNotes} onChange={e => setResolveNotes(e.target.value)}
                  className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#E8956D] resize-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setResolveModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleResolveComplaint} disabled={!resolveNotes.trim()}
                  className="flex-1 bg-[#637C64] text-white rounded-lg py-2.5 text-sm font-bold disabled:opacity-50">
                  Mark Resolved
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
